/**
 * Date format utilities for consistent date handling across the application
 */

import { parse, format, isValid, parseISO } from 'date-fns'

const DATE_FORMATS = {
  API: {
    DATE: 'yyyy-MM-dd',
    DATETIME: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  },
  DISPLAY: {
    DATE: 'dd.MM.yyyy',
    DATETIME: 'dd.MM.yyyy HH:mm:ss',
  },
} as const

/**
 * Determines if a date string or object includes time information
 */
function hasTimeComponent(date: string | Date): boolean {
  if (date instanceof Date) {
    return (
      date.getHours() !== 0 ||
      date.getMinutes() !== 0 ||
      date.getSeconds() !== 0 ||
      date.getMilliseconds() !== 0
    )
  }
  return date.includes('T') || (date.includes(' ') && /\d{2}:\d{2}/.test(date))
}

/**
 * Converts any supported date format to API format
 * Supports:
 * - YYYY-MM-DD
 * - YYYY-MM-DDTHH:mm:ss.SSSZ
 * - DD.MM.YYYY
 * - DD.MM.YYYY HH:mm:ss
 * - Date object
 * - ISO string
 *
 * Preserves time information if present in the input
 */
export function serializeDate(
  date: string | Date | null | undefined,
): string | null {
  if (!date) return null

  try {
    let dateObj: Date

    if (date instanceof Date) {
      dateObj = date
    } else if (typeof date === 'string') {
      // Try parsing as DD.MM.YYYY or DD.MM.YYYY HH:mm:ss
      let parsedDate = parse(date, DATE_FORMATS.DISPLAY.DATETIME, new Date())

      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.DISPLAY.DATE, new Date())
      }

      // If that fails, try parsing as YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.SSSZ
      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.API.DATETIME, new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.API.DATE, new Date())
      }

      // If all fails, try parsing as ISO
      if (!isValid(parsedDate)) {
        parsedDate = parseISO(date)
      }

      if (!isValid(parsedDate)) {
        return null
      }

      dateObj = parsedDate
    } else {
      return null
    }

    // Use datetime format if the input had time information
    const formatString = hasTimeComponent(date)
      ? DATE_FORMATS.API.DATETIME
      : DATE_FORMATS.API.DATE
    return format(dateObj, formatString)
  } catch {
    return null
  }
}

/**
 * Converts any supported date format to display format
 * Supports:
 * - YYYY-MM-DD
 * - YYYY-MM-DDTHH:mm:ss.SSSZ
 * - DD.MM.YYYY
 * - DD.MM.YYYY HH:mm:ss
 * - Date object
 * - ISO string
 *
 * Preserves time information if present in the input
 */
export function formatDate(
  date: string | Date | null | undefined,
): string | null {
  if (!date) return null

  try {
    let dateObj: Date

    if (date instanceof Date) {
      dateObj = date
    } else if (typeof date === 'string') {
      // Try parsing as YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.SSSZ
      let parsedDate = parse(date, DATE_FORMATS.API.DATETIME, new Date())

      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.API.DATE, new Date())
      }

      // If that fails, try parsing as DD.MM.YYYY or DD.MM.YYYY HH:mm:ss
      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.DISPLAY.DATETIME, new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(date, DATE_FORMATS.DISPLAY.DATE, new Date())
      }

      // If all fails, try parsing as ISO
      if (!isValid(parsedDate)) {
        parsedDate = parseISO(date)
      }

      if (!isValid(parsedDate)) {
        return null
      }

      dateObj = parsedDate
    } else {
      return null
    }

    // Use datetime format if the input had time information
    const formatString = hasTimeComponent(date)
      ? DATE_FORMATS.DISPLAY.DATETIME
      : DATE_FORMATS.DISPLAY.DATE
    return format(dateObj, formatString)
  } catch {
    return null
  }
}

/**
 * Checks if the provided string is a valid date in any supported format
 */
export function isValidDate(date: string | Date | null | undefined): boolean {
  if (!date) return false

  try {
    if (date instanceof Date) {
      return isValid(date)
    }

    if (typeof date === 'string') {
      // Try all supported formats
      const formats = [
        DATE_FORMATS.API.DATE,
        DATE_FORMATS.API.DATETIME,
        DATE_FORMATS.DISPLAY.DATE,
        DATE_FORMATS.DISPLAY.DATETIME,
      ]

      for (const formatStr of formats) {
        const parsed = parse(date, formatStr, new Date())
        if (isValid(parsed)) return true
      }

      // Try ISO format
      const isoDate = parseISO(date)
      return isValid(isoDate)
    }

    return false
  } catch {
    return false
  }
}
